import {
  Alert,
  Button,
  Breadcrumb,
  Card,
  Col,
  Form,
  Input,
  InputNumber,
  Radio,
  Row,
  Select,
  Space,
  Spin,
  Switch,
  Tag,
} from "antd";
import {
  FolderOutlined,
  HomeOutlined,
  LinkOutlined,
} from "@ant-design/icons";
import { useContext, useEffect, useMemo, useState } from "react";
import { Link, useHistory, useParams } from "react-router-dom";
import { openNotification } from "../../../components/Notification";
import PageHeader from "../../../components/PageHeader";
import { UserContext } from "../../../contexts/UserContext";
import { menuIndicadorService } from "../../../services/resources/menuIndicadorService";
import { podeGerenciarMenuIndicador, urls } from "../../../utils";

function achatar(nos, nivel = 0, excluir) {
  const lista = [];
  (nos || []).forEach((no) => {
    if (excluir && no.codigo === excluir) return;
    lista.push({
      label: `${"— ".repeat(nivel)}${no.nome}`,
      value: no.codigo,
    });
    lista.push(...achatar(no.filhos, nivel + 1, excluir));
  });
  return lista;
}

const cardStyle = {
  border: "1px solid var(--border-color)",
  borderRadius: 8,
  marginBottom: 16,
};

export const FormularioMenuIndicador = ({ onCreate, onEdit }) => {
  const [form] = Form.useForm();
  const history = useHistory();
  const { codigo } = useParams();
  const { user } = useContext(UserContext);
  const autorizado = podeGerenciarMenuIndicador(user);
  const [loading, setLoading] = useState(false);
  const [arvore, setArvore] = useState([]);
  const [grupos, setGrupos] = useState([]);
  const [permissoes, setPermissoes] = useState([]);
  const tipo = Form.useWatch("tipo", form);

  useEffect(() => {
    if (!autorizado) return;
    carregarApoio();
    if (codigo) carregarItem();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autorizado, codigo]);

  async function carregarApoio() {
    try {
      const [{ data: tree }, { data: gruposData }, { data: permissoesData }] =
        await Promise.all([
          menuIndicadorService.listarAdmin(),
          menuIndicadorService.listarGrupos(),
          menuIndicadorService.listarPermissoes(),
        ]);
      setArvore(tree || []);
      setGrupos(gruposData || []);
      setPermissoes(permissoesData || []);
    } catch (error) {
      openNotification(
        "error",
        <strong>Erro ao carregar dados</strong>,
        error?.response?.data?.userMessage
      );
    }
  }

  async function carregarItem() {
    setLoading(true);
    try {
      const { data } = await menuIndicadorService.get(codigo);
      form.setFieldsValue({
        nome: data.nome,
        tipo: data.url ? "LINK" : "PASTA",
        url: data.url,
        codigoPai: data.codigoPai,
        ordem: data.ordem,
        ativo: data.ativo,
        permissaoNome: data.permissaoNome,
        codigosGrupos: (data.grupos || []).map((grupo) => grupo.codigo),
      });
    } catch (error) {
      openNotification(
        "error",
        <strong>Erro ao carregar item</strong>,
        error?.response?.data?.userMessage
      );
    } finally {
      setLoading(false);
    }
  }

  async function salvar() {
    try {
      const values = await form.validateFields();
      setLoading(true);
      const payload = {
        nome: values.nome,
        url: values.tipo === "LINK" ? values.url : null,
        codigoPai: values.codigoPai || null,
        ordem: values.ordem ?? 0,
        ativo: values.ativo,
        permissaoNome: values.permissaoNome || null,
        codigosGrupos: values.codigosGrupos || [],
      };
      if (onCreate) {
        await menuIndicadorService.criar(payload);
        openNotification("success", <strong>Item criado</strong>);
      } else {
        await menuIndicadorService.atualizar(codigo, payload);
        openNotification("success", <strong>Item atualizado</strong>);
      }
      history.push(urls.MENUS_INDICADORES_LIST);
    } catch (error) {
      if (error?.errorFields) return;
      openNotification(
        "error",
        <strong>Não foi possível salvar</strong>,
        error?.response?.data?.userMessage || error?.response?.data?.detail
      );
    } finally {
      setLoading(false);
    }
  }

  const opcoesPai = useMemo(() => achatar(arvore, 0, codigo), [arvore, codigo]);

  if (!autorizado) {
    return (
      <div id="sectionMain">
        <Alert type="error" showIcon message="Acesso restrito ao grupo OISOL MASTER" />
      </div>
    );
  }

  return (
    <div id="sectionMain">
      <Breadcrumb id="breadcrumb">
        <Breadcrumb.Item>
          <Link to="">
            <HomeOutlined />
          </Link>
        </Breadcrumb.Item>
        <Breadcrumb.Item>gerenciamento</Breadcrumb.Item>
        <Breadcrumb.Item>
          <Link to={urls.MENUS_INDICADORES_LIST}>menus de indicadores</Link>
        </Breadcrumb.Item>
        <Breadcrumb.Item>
          <strong>{onCreate ? "novo item" : "editar item"}</strong>
        </Breadcrumb.Item>
      </Breadcrumb>
      <div className="container-section">
        <PageHeader
          titulo={onCreate ? "NOVO ITEM DE INDICADOR" : "EDITAR ITEM DE INDICADOR"}
          subtitulo="Defina o nome, o tipo (pasta ou link) e quem pode ver o item no menu."
        />
        <Spin spinning={loading}>
          <Form
            form={form}
            layout="vertical"
            initialValues={{ ativo: true, ordem: 0, tipo: "LINK" }}
          >
            <Card title="Identificação" size="small" style={cardStyle}>
              <Row gutter={[16, 0]}>
                <Col xs={24} md={16}>
                  <Form.Item
                    name="nome"
                    label="Nome no menu"
                    rules={[{ required: true, message: "Informe o nome" }]}
                  >
                    <Input maxLength={200} placeholder="Ex.: CEMARIS" />
                  </Form.Item>
                </Col>
                <Col xs={12} md={4}>
                  <Form.Item name="ordem" label="Ordem">
                    <InputNumber min={0} style={{ width: "100%" }} />
                  </Form.Item>
                </Col>
                <Col xs={12} md={4}>
                  <Form.Item name="ativo" label="Situação" valuePropName="checked">
                    <Switch checkedChildren="ativo" unCheckedChildren="inativo" />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item name="codigoPai" label="Item pai">
                    <Select
                      allowClear
                      placeholder="Raiz do menu Indicadores"
                      options={opcoesPai}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Card>

            <Card
              title="Tipo do item"
              size="small"
              style={cardStyle}
              extra={
                tipo === "PASTA" ? (
                  <Tag icon={<FolderOutlined />} color="orange">
                    pasta
                  </Tag>
                ) : (
                  <Tag icon={<LinkOutlined />} color="blue">
                    link
                  </Tag>
                )
              }
            >
              <Form.Item name="tipo" style={{ marginBottom: 12 }}>
                <Radio.Group optionType="button" buttonStyle="solid">
                  <Radio.Button value="LINK">
                    <LinkOutlined /> Link do Metabase
                  </Radio.Button>
                  <Radio.Button value="PASTA">
                    <FolderOutlined /> Pasta com submenu
                  </Radio.Button>
                </Radio.Group>
              </Form.Item>
              {tipo === "LINK" ? (
                <Form.Item
                  name="url"
                  label="URL externa"
                  extra="Somente endereços http:// ou https://."
                  rules={[
                    { required: true, message: "Informe a URL do painel" },
                  ]}
                >
                  <Input
                    prefix={<LinkOutlined />}
                    maxLength={500}
                    placeholder="https://dados.oisol.sps.ce.gov.br/public/dashboard/..."
                  />
                </Form.Item>
              ) : (
                <Alert
                  type="info"
                  showIcon
                  message="Pasta"
                  description="Não possui URL. Use para agrupar outros itens no menu em cascata."
                />
              )}
            </Card>

            <Card title="Quem pode ver" size="small" style={cardStyle}>
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
                message="Permissão e grupo podem ser usados juntos. Grupo na pasta vale para todos os filhos."
              />
              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="permissaoNome"
                    label="Permissão"
                    extra="Authority já existente no sistema (opcional)."
                  >
                    <Select
                      allowClear
                      showSearch
                      optionFilterProp="label"
                      placeholder="Nenhuma"
                      options={permissoes.map((item) => ({
                        label: item.nome,
                        value: item.nome,
                      }))}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="codigosGrupos"
                    label="Grupos"
                    extra="Se vazio, vale só a permissão (quando houver)."
                  >
                    <Select
                      mode="multiple"
                      allowClear
                      optionFilterProp="label"
                      placeholder="Nenhum grupo específico"
                      options={grupos.map((grupo) => ({
                        label: grupo.nome,
                        value: grupo.codigo,
                      }))}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Card>
          </Form>
          <Row justify="end">
            <Space>
              <Button onClick={() => history.push(urls.MENUS_INDICADORES_LIST)}>
                Cancelar
              </Button>
              <Button type="primary" onClick={salvar}>
                Salvar
              </Button>
            </Space>
          </Row>
        </Spin>
      </div>
    </div>
  );
};
